import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import VehicleFuelUsageAddEdit from "./VehicleFuelUsageAddEdit"
import { VehicleFuelUsageListHeader } from "./VehicleFuelUsageListHeader"

import { useDeleteVehicleFuelStock } from "hooks/useVehicleFuelStocks"
import { useListParams } from "hooks/useListParams"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import PageWrapper from "components/Common/PageWrapper"
import { formatReadableDate } from "utils/formatDate"
import VehicleInfoCell from "components/Common/VehicleInfo/VehicleInfoCell"
import ActionButtonsCell from "components/Common/ActionButtonsCell"
import FuelUsageSummary from "./FuelUsageSummary"
import { useVehicleFuelUses } from "hooks/useVehicleFuelUses"
import FuelUsageInfoCell from "components/Common/VehicleFuel/FuelUsageInfoCell"

const VehicleFuelUsageList = () => {
  document.title = "Fuel Usage List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [fuelUsage, setFuelStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const {
    params,
    setPage,
    setSearch,
    resetParams,
    updateParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
    vehicle_id: "",
    fuel_type_id: "",
    fuel_station_id: "",
    date_from: "",
    date_to: "",
  })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      search: params.search,
      vehicle_id: params.vehicle_id,
      fuel_type_id: params.fuel_type_id,
      fuel_station_id: params.fuel_station_id,
      date_from: params.date_from,
      date_to: params.date_to,
    }),
    [params]
  )

  const {
    data: fuelStocksResponse,
    isLoading,
    refetch: refetchFuelStocks,
    isRefetching,
  } = useVehicleFuelUses(queryParams)

  const { mutateAsync: deleteFuelStock, isPending: deleteLoading } =
    useDeleteVehicleFuelStock()

  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()

  const locationsData = useMemo(
    () => rawData?.locations ?? [],
    [rawData?.locations]
  )
  const employeesData = useMemo(
    () => rawData?.employees ?? [],
    [rawData?.employees]
  )
  const fuelTypesData = useMemo(
    () => rawData?.fuel_types ?? [],
    [rawData?.fuel_types]
  )

  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setFuelStock(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFuelStock(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setFuelStock(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleDeleteClick = useCallback(item => {
    setFuelStock(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!fuelUsage?.id) return
    try {
      const res = await deleteFuelStock(fuelUsage.id)
      toast.success(res?.message ?? "Usage fuel deleted successfully.")
      setDeleteModal(false)
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete fuel stock.")
    }
  }, [fuelUsage, deleteFuelStock])

  const handleRefresh = () => refetchFuelStocks()

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "id",
      },
      {
        header: "Vehicle Info",
        accessorKey: "vehicle_info",
        cell: ({ row }) => (
          <div style={{ minWidth: 320 }} className="text-wrap">
            <VehicleInfoCell data={row.original?.vehicle} />
          </div>
        ),
      },
      {
        header: "Fuel Usage Info",
        accessorKey: "station_purchaser",
        cell: ({ row }) => (
          <div style={{ minWidth: 320, maxWidth: 320 }} className="text-wrap">
            <FuelUsageInfoCell data={row.original} />
          </div>
        ),
      },
      {
        header: "Dates",
        accessorKey: "dates",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          const buttons = [
            {
              type: "edit",
              btnId: `edit-${data.stock_id}`,
              btnName: "Edit Stock",
              onClick: () => handleEditClick(data),
            },
            {
              type: "delete",
              btnId: `delete-${data.id}`,
              btnName: "Delete Stock",
              onClick: () => handleDeleteClick(data),
            },
          ]

          return <ActionButtonsCell buttons={buttons} />
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  )

  const data = fuelStocksResponse?.data?.data?.data || []
  const paginationInfo = fuelStocksResponse?.data?.data
  const totals = fuelStocksResponse?.data?.totals
  const summary = fuelStocksResponse?.data?.summary
  const comparison_basis = fuelStocksResponse?.data?.periods?.comparison_basis

  return (
    <PageWrapper title="Usage Fuel" breadcrumbItem="Usage Fuel List">
      <VehicleFuelUsageListHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div>
        <FuelUsageSummary
          totals={totals}
          summary={summary}
          comparison_basis={comparison_basis}
        />
      </div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <VehicleFuelUsageAddEdit
        modal={modal}
        isEdit={isEdit}
        fuelUsage={fuelUsage}
        toggleModal={toggleModal}
        fuelTypes={fuelTypesData}
        locations={locationsData}
        employees={employeesData}
        optionLoading={optionLoading}
      />
    </PageWrapper>
  )
}

export default VehicleFuelUsageList
